import {
  useMarkAllRead,
  useMarkRead,
  useNotifications,
  useUnreadCount as useUnreadCountQuery,
} from '@sazeo/sdk';
import { Bell } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useRouteLoaderData } from 'react-router-dom';

import type { RootLoaderData } from '../App';
import { tenantSdkClient } from '../lib/sdk';

export function NotificationBell() {
  const { t } = useTranslation('core.notifications');
  const loaderData = useRouteLoaderData('root') as RootLoaderData | undefined;
  const tenantId = loaderData?.tenant.id ?? '';
  const ref = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const unreadQuery = useUnreadCountQuery(tenantSdkClient, tenantId);
  const unreadCount = unreadQuery.data?.count ?? 0;
  const notificationsQuery = useNotifications(tenantSdkClient, tenantId, { limit: 20 });
  const notifications = notificationsQuery.data?.data ?? [];
  const markAllRead = useMarkAllRead(tenantSdkClient, tenantId);
  const markRead = useMarkRead(tenantSdkClient, tenantId);

  useEffect(() => {
    if (!open) return;

    function handlePointerDown(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    document.addEventListener('mousedown', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [open]);

  const badgeLabel = unreadCount > 99 ? '99+' : String(unreadCount);
  const panelBody = useMemo(() => {
    if (notificationsQuery.isError) {
      return (
        <li className="px-4 py-6 text-center text-[12px] text-[var(--color-text-tertiary)]">
          {t('load_failed')}
        </li>
      );
    }
    if (notificationsQuery.isLoading) {
      return (
        <li className="px-4 py-6 text-center text-[12px] text-[var(--color-text-tertiary)]">
          {t('loading')}
        </li>
      );
    }
    if (notifications.length === 0) {
      return (
        <li className="px-4 py-6 text-center text-[12px] text-[var(--color-text-tertiary)]">
          {t('empty')}
        </li>
      );
    }

    return notifications.map((notification) => {
      const body =
        (typeof notification.body === 'string' && notification.body.trim()) ||
        (typeof notification.title === 'string' && notification.title.trim()) ||
        t('empty_notification');

      function handleRowClick() {
        setOpen(false);
        if (!notification.read_at) {
          void markRead.mutate(notification.id);
        }
        if (notification.action_url) {
          navigate(notification.action_url);
        }
      }

      return (
        <li
          key={notification.id}
          data-testid="notification-item"
          data-module="inbox"
          data-feature="notifications"
          data-component="NotificationBell"
          data-file="apps/tenant-app/src/components/NotificationBell.tsx"
          data-entity="notification"
          data-entity-id={notification.id}
          data-unread={notification.read_at ? 'false' : 'true'}
          onClick={handleRowClick}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') handleRowClick();
          }}
          className={`flex gap-3 px-4 py-3 text-[12px] ${notification.action_url ? 'cursor-pointer hover:bg-[var(--color-surface)]' : ''}`}
        >
          <span
            aria-hidden
            className={
              notification.read_at
                ? 'mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-transparent'
                : 'mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--color-accent)]'
            }
          />
          <div
            className={
              notification.read_at
                ? 'min-w-0 flex-1'
                : 'min-w-0 flex-1 rounded-[4px] bg-[var(--color-surface)] px-2 py-1.5'
            }
          >
            <p className="truncate text-[var(--color-text-primary)]">{body}</p>
            <time className="text-[11px] text-[var(--color-text-tertiary)]">
              {new Date(notification.created_at).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </time>
          </div>
        </li>
      );
    });
  }, [
    markRead,
    navigate,
    notifications,
    notificationsQuery.isError,
    notificationsQuery.isLoading,
    t,
  ]);

  return (
    <div
      ref={ref}
      className="relative"
      data-component="NotificationBell"
      data-file="apps/tenant-app/src/components/NotificationBell.tsx"
    >
      <button
        type="button"
        aria-label={t('bell_label', { count: unreadCount })}
        data-testid="notification-bell"
        data-module="inbox"
        data-feature="notifications"
        data-component="NotificationBell"
        data-file="apps/tenant-app/src/components/NotificationBell.tsx"
        data-action="toggle-panel"
        data-state={open ? 'open' : 'closed'}
        onClick={() => setOpen((value) => !value)}
        className="relative inline-flex h-8 w-8 items-center justify-center rounded-[var(--radius)] text-[var(--color-text-secondary)] hover:bg-[var(--color-surface)]"
      >
        <Bell size={16} aria-hidden />
        {unreadCount > 0 && (
          <span
            aria-hidden
            className="absolute right-0.5 top-0.5 flex h-4 min-w-4 items-center justify-center rounded-[var(--radius-full,9999px)] bg-[var(--color-accent)] px-1 font-mono text-[9px] font-semibold text-[var(--color-bg)]"
          >
            {badgeLabel}
          </span>
        )}
      </button>

      {open && (
        <dialog
          open
          aria-label={t('panel_label')}
          data-testid="notification-panel"
          data-module="inbox"
          data-feature="notifications"
          data-component="NotificationBell"
          data-file="apps/tenant-app/src/components/NotificationBell.tsx"
          className="absolute right-0 top-[calc(100%+4px)] z-[200] w-80 overflow-hidden rounded-[var(--radius)] border border-[var(--color-border)] bg-[var(--color-surface)] shadow-md"
          onKeyDown={(e) => {
            if (e.key === 'Escape') setOpen(false);
          }}
        >
          <div className="flex items-center justify-between border-b border-[var(--color-border)] px-4 py-2">
            <span className="text-[13px] font-semibold text-[var(--color-text-primary)]">
              {t('panel_title')}
            </span>
            {unreadCount > 0 && (
              <button
                type="button"
                data-testid="notification-mark-all-read-button"
                data-module="inbox"
                data-feature="notifications"
                data-component="NotificationBell"
                data-file="apps/tenant-app/src/components/NotificationBell.tsx"
                data-action="mark-all-read"
                onClick={() => {
                  void markAllRead.mutateAsync().then(() => {
                    setOpen(true);
                  });
                }}
                disabled={markAllRead.isPending}
                className="text-[11px] text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] disabled:opacity-50"
              >
                {t('mark_all_read')}
              </button>
            )}
          </div>

          <ul className="max-h-72 overflow-y-auto divide-y divide-[var(--color-border)]">
            {panelBody}
          </ul>

          <div className="border-t border-[var(--color-border)] px-4 py-2">
            <Link
              to="/inbox"
              data-testid="notification-open-inbox-link"
              data-module="inbox"
              data-feature="notifications"
              data-component="NotificationBell"
              data-file="apps/tenant-app/src/components/NotificationBell.tsx"
              data-action="open-inbox"
              onClick={() => setOpen(false)}
              className="block text-center text-[12px] text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
            >
              {t('view_all')}
            </Link>
          </div>
        </dialog>
      )}
    </div>
  );
}
